/**
 * Προτάσεις πώλησης (upsell) — η οθόνη των κεντρικών.
 *
 * Ο υπεύθυνος του οργανισμού γράφει ΜΙΑ φορά το μήνυμα και τα κεντρικά προϊόντα· το ταμείο
 * κάθε καταστήματος δείχνει ΜΙΑ πρόταση (την πρώτη της λίστας που ταιριάζει) όταν ο ταμίας
 * πατά «Ολοκλήρωση» (Take Away) ή «Checkout» (τηλεφωνικό delivery). Σειρά λίστας = προτεραιότητα
 * (μικρότερο priority εμφανίζεται πρώτο).
 *
 * Ένας διακόπτης για όλο τον οργανισμό, κλειστός αρχικά: όσο είναι κλειστός κανένα ταμείο δεν
 * δείχνει τίποτα, όσες προτάσεις κι αν είναι «Ενεργές».
 *
 * Το PUT του server αντικαθιστά ΟΛΑ τα πεδία και τις συνδέσεις (προϊόντα/κανόνα/καταστήματα),
 * γι' αυτό κάθε αποθήκευση — και η αλλαγή σειράς — στέλνει πλήρες σώμα (toPayload).
 * Η διαγραφή σβήνει ΚΑΙ τα στατιστικά (cascade): προτείνουμε απενεργοποίηση.
 *
 * Νέα εμφάνιση (14/10/2026): σελίδα «Πίνακας» (shared/listPage). Ο διακόπτης του οργανισμού είναι
 * γραμμή του «Φύλλου» (SheetRow — όχι πράσινο κουτί), οι προτάσεις λίστα με λεπτή γραμμή ανάμεσα και η
 * φόρμα συρτάρι δεξιά (DetailPanel). Η λίστα ΔΕΝ είναι DataTable: η σειρά αλλάζει με ▲/▼ μέσα στη
 * γραμμή και κάθε πρόταση έχει μήνυμα, προϊόντα και τέσσερις περιλήψεις, που σε στήλες δεν χωρούν.
 * Η αναφορά μένει <table>: κάθε γραμμή της ανοίγει σε ανάλυση ανά κατάστημα και ανά ταμία.
 * Ίδια endpoints, ίδια σώματα, ίδιοι έλεγχοι και ίδια δικαιώματα με πριν — άλλαξε μόνο η όψη.
 */

import React, { useState, useEffect, useCallback, useId, useMemo, useRef } from 'react';
import { organizationAPI } from '../services/api';
import {
  Check, ChevronDown, ChevronRight, ChevronUp, Loader2, Pencil, Plus, Search, Sparkles, Trash2, X,
} from 'lucide-react';
import { LoadFailed } from './settings/instantSettings';
import { SettingsSwitch } from './settings/storeSettingsForm';
import { SheetField, SheetInherit, SheetRow } from './settings/sheet';
import {
  ASIDE_BTN, DANGER_GHOST_BTN, DateRange, DatePreset, DateRangeValue, DetailPanel, EmptyState, GHOST_BTN, INPUT_CLS,
  ListPage, ListToolbar, PRIMARY_BTN, StatusChip, presetRange,
} from './shared/listPage';
import { Banner, COMPACT_INPUT, GroupTitle, LINK_BTN, ROW_DANGER_BTN, ROW_ICON_BTN, chipClass } from './orgPages/ui';

interface Props {
  orgId: number;
  /** owner/admin/editor: δημιουργία, επεξεργασία, σειρά, ενεργοποίηση */
  canEdit: boolean;
  /** owner/admin: διακόπτης οργανισμού και διαγραφή */
  canAdmin: boolean;
}

type Channel = 'take_away' | 'delivery';
type TriggerMode = 'always' | 'cart_contains';

interface Suggestion {
  id: number;
  name: string;
  message: string;
  trigger_mode: TriggerMode;
  channels: Channel[];
  starts_at: string | null;
  ends_at: string | null;
  days_of_week: number[] | null;
  time_from: string | null;
  time_to: string | null;
  priority: number;
  is_active: boolean;
  org_product_ids: number[];
  trigger_org_product_ids: number[];
  trigger_org_category_ids: number[];
  store_ids: number[];
}

interface Stat {
  shown: number;
  accepted: number;
  items: number;
  revenue: number;
  rate: number | null;
}

interface Report {
  from: string;
  to: string;
  suggestions: Array<Stat & { id: number; name: string; is_active: boolean }>;
  by_store: Array<Stat & { suggestion_id: number; store_id: number; store: string }>;
  by_cashier: Array<Stat & { suggestion_id: number; employee_id: number | null; cashier: string | null }>;
}

interface CatalogProduct {
  id: number;
  name: string;
  category_name: string | null;
  is_available: boolean;
}

interface CatalogCategory {
  id: number;
  name: string;
  depth: number;
  /** «Γονική › Υποκατηγορία» — για τις περιλήψεις */
  label: string;
}

interface StoreLite {
  id: number;
  name: string;
}

// Ίδια όρια με το services/upsellSuggestions.js του server.
const NAME_MAX = 120;
const MESSAGE_MAX = 240;
const MAX_PRODUCTS = 6;

const CHANNELS: Array<{ id: Channel; label: string; short: string }> = [
  { id: 'take_away', label: 'Take Away', short: 'Take Away' },
  { id: 'delivery', label: 'Delivery (τηλεφωνικό)', short: 'Delivery' },
];

// Δευτέρα πρώτη στην οθόνη· οι τιμές είναι του PostgreSQL (EXTRACT DOW: 0 = Κυριακή).
const DAY_CHIPS: Array<[number, string]> = [
  [1, 'Δευ'], [2, 'Τρι'], [3, 'Τετ'], [4, 'Πεμ'], [5, 'Παρ'], [6, 'Σαβ'], [0, 'Κυρ'],
];

const EMPTY_FORM = {
  name: '',
  message: '',
  trigger_mode: 'always' as TriggerMode,
  channels: ['take_away', 'delivery'] as Channel[],
  starts_at: '',
  ends_at: '',
  days_of_week: [] as number[],
  time_from: '',
  time_to: '',
  is_active: true,
  org_product_ids: [] as number[],
  trigger_org_product_ids: [] as number[],
  trigger_org_category_ids: [] as number[],
  all_stores: true,
  store_ids: [] as number[],
};
type FormState = typeof EMPTY_FORM;

type LoadState = 'loading' | 'ready' | 'error';

const LOAD_FAILED = 'Αποτυχία φόρτωσης προτάσεων πώλησης';

/**
 * Περίοδος της αναφοράς: 7 ή 30 ημέρες ή «Προσαρμογή…», χωρίς «Όλες» (ο server θέλει πάντα από–έως).
 * Η προεπιλογή «30 ημ.» (σήμερα και 29 μέρες πίσω, ώρα Ελλάδας) είναι η ίδια με πριν και με τον server.
 */
const REPORT_PRESETS: DatePreset[] = ['last7', 'last30'];

/** Ημερομηνία (YYYY-MM-DD) σε ώρα Ελλάδας — όπως οι προεπιλογές της αναφοράς στον server. */
const athensDate = (daysAgo = 0) =>
  new Date(Date.now() - daysAgo * 864e5).toLocaleDateString('sv-SE', { timeZone: 'Europe/Athens' });

/** Αναζήτηση χωρίς τόνους/πεζά-κεφαλαία («ΚΟΥΛΟΎΡΙ» βρίσκει «κουλουρι»). */
const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const fmtDate = (d: string) => {
  const [y, m, dd] = d.slice(0, 10).split('-');
  return `${dd}/${m}/${y}`;
};
const fmtEur = (n: number) =>
  new Intl.NumberFormat('el-GR', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);
const fmtNum = (n: number) => (Number(n) || 0).toLocaleString('el-GR', { maximumFractionDigits: 2 });
const fmtRate = (r: number | null) =>
  r == null ? '—' : `${Number(r).toLocaleString('el-GR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

const formFrom = (s: Suggestion): FormState => ({
  name: s.name,
  message: s.message,
  trigger_mode: s.trigger_mode,
  channels: [...(s.channels || [])],
  starts_at: s.starts_at ? String(s.starts_at).slice(0, 10) : '',
  ends_at: s.ends_at ? String(s.ends_at).slice(0, 10) : '',
  days_of_week: [...(s.days_of_week || [])],
  time_from: s.time_from || '',
  time_to: s.time_to || '',
  is_active: s.is_active,
  org_product_ids: [...(s.org_product_ids || [])],
  trigger_org_product_ids: [...(s.trigger_org_product_ids || [])],
  trigger_org_category_ids: [...(s.trigger_org_category_ids || [])],
  all_stores: (s.store_ids || []).length === 0,
  store_ids: [...(s.store_ids || [])],
});

/** Πλήρες σώμα POST/PUT (ο server αντικαθιστά τα πάντα). store_ids κενό = όλα τα καταστήματα. */
const toPayload = (f: FormState, priority: number) => ({
  name: f.name.trim(),
  message: f.message.trim(),
  trigger_mode: f.trigger_mode,
  channels: CHANNELS.map(c => c.id).filter(c => f.channels.includes(c)),
  starts_at: f.starts_at || null,
  ends_at: f.ends_at || null,
  // Καμία ή και οι 7 μέρες = κάθε μέρα (null).
  days_of_week: f.days_of_week.length > 0 && f.days_of_week.length < 7
    ? [...f.days_of_week].sort((a, b) => a - b) : null,
  time_from: f.time_from || null,
  time_to: f.time_to || null,
  priority,
  is_active: f.is_active,
  org_product_ids: f.org_product_ids,
  trigger_org_product_ids: f.trigger_mode === 'cart_contains' ? f.trigger_org_product_ids : [],
  trigger_org_category_ids: f.trigger_mode === 'cart_contains' ? f.trigger_org_category_ids : [],
  store_ids: f.all_stores ? [] : f.store_ids,
});

/** Ο ίδιος έλεγχος με τον server (validateSuggestion), για να μη φεύγει άσκοπο αίτημα. */
const validate = (f: FormState): string | null => {
  if (!f.name.trim()) return 'Δώσε όνομα στην πρόταση';
  if (f.name.trim().length > NAME_MAX) return `Το όνομα είναι πολύ μεγάλο (έως ${NAME_MAX} χαρακτήρες)`;
  if (!f.message.trim()) return 'Γράψε το μήνυμα που θα δει ο ταμίας';
  if (f.message.trim().length > MESSAGE_MAX) return `Το μήνυμα είναι πολύ μεγάλο (έως ${MESSAGE_MAX} χαρακτήρες)`;
  if (f.org_product_ids.length === 0) return 'Διάλεξε τουλάχιστον ένα προϊόν για πρόταση';
  if (f.org_product_ids.length > MAX_PRODUCTS) return `Έως ${MAX_PRODUCTS} προϊόντα ανά πρόταση`;
  if (f.trigger_mode === 'cart_contains'
      && f.trigger_org_product_ids.length + f.trigger_org_category_ids.length === 0) {
    return 'Για «Μόνο αν το καλάθι έχει…» διάλεξε προϊόντα ή κατηγορίες';
  }
  if (f.channels.length === 0) return 'Διάλεξε Take Away ή/και Delivery';
  if (!f.all_stores && f.store_ids.length === 0) return 'Διάλεξε καταστήματα ή «Όλα τα καταστήματα»';
  if (f.starts_at && f.ends_at && f.ends_at < f.starts_at) return 'Η λήξη είναι πριν από την έναρξη';
  if (Boolean(f.time_from) !== Boolean(f.time_to)) return 'Συμπλήρωσε και τις δύο ώρες (ή καμία)';
  if (f.time_from && f.time_from === f.time_to) return 'Οι ώρες «από» και «έως» είναι ίδιες';
  return null;
};

/** Δέντρο κατηγοριών σε επίπεδη σειρά (γονική και αμέσως μετά οι υποκατηγορίες της). */
function buildCategoryTree(rows: any[]): CatalogCategory[] {
  const ids = new Set(rows.map(c => c.id));
  const out: CatalogCategory[] = [];
  const seen = new Set<number>();
  const walk = (c: any, depth: number, path: string[]) => {
    if (seen.has(c.id)) return;
    seen.add(c.id);
    out.push({ id: c.id, name: c.name, depth, label: [...path, c.name].join(' › ') });
    rows.filter(x => x.parent_id === c.id).forEach(x => walk(x, depth + 1, [...path, c.name]));
  };
  rows.filter(c => c.parent_id == null || !ids.has(c.parent_id)).forEach(c => walk(c, 0, []));
  return out;
}

// ── Όψη: κλάσεις της νέας εμφάνισης (μόνο tokens) ────────────────────────────

/** Μικρό κεφαλαίο υπο-ομάδας («Ανά κατάστημα») — ίδιο με το GroupTitle, ένα επίπεδο πιο κάτω. */
const SUB_TITLE = 'text-[11.5px] font-bold uppercase tracking-[0.06em] text-t3';
/** Επικεφαλίδα/κελί της αναφοράς: ίδια όψη με τον «Πίνακα» (listPage), χωρίς κουτί. Το χρώμα μπαίνει
 *  ανά κελί — δύο text-* στο ίδιο στοιχείο τα διαλέγει η σειρά του CSS, όχι η σειρά της κλάσης. */
const TH = 'px-3 py-2 text-[12.5px] font-semibold leading-[18px] text-t3 whitespace-nowrap';
const TD = 'px-3 py-2.5 text-right tabular-nums';
/** Γραμμή επιλογής (προϊόν, κατηγορία): επιλεγμένη στο απαλό χρώμα της εφαρμογής, όχι μπλε. */
const PICK_ROW = 'flex w-full items-center gap-2.5 rounded-lg py-2 pr-2.5 text-left text-sm transition-colors '
  + 'disabled:cursor-not-allowed disabled:opacity-40';
const pickTone = (on: boolean) => (on ? 'bg-brand-soft text-t1' : 'text-t2 hover:bg-surface-2');
const CHECKBOX = 'h-4 w-4 shrink-0 accent-[hsl(var(--brand))]';

// ── Μικρά δομικά κομμάτια ────────────────────────────────────────────────────

/** Ετικέτα → τιμή στη γραμμή meta μιας πρότασης. Κάθε τιμή στο δικό της στοιχείο (dd). */
const Meta: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex min-w-0 max-w-full items-baseline gap-1.5">
    <dt className="shrink-0 text-t3">{label}:</dt>
    <dd className="min-w-0 break-words text-t2">{children}</dd>
  </div>
);

/** Το «κουτάκι» μιας γραμμής επιλογής (η γραμμή είναι κουμπί με aria-pressed, όχι checkbox). */
const PickBox: React.FC<{ on: boolean }> = ({ on }) => (
  <span aria-hidden="true"
    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${on ? 'border-brand bg-brand' : 'border-line-strong bg-surface-2'}`}>
    {on && <Check className="h-3 w-3 text-brand-fg" strokeWidth={3} />}
  </span>
);

/** Λίστα προϊόντων με αναζήτηση· πάτημα = επιλογή/αποεπιλογή. */
const ProductPicker: React.FC<{
  products: CatalogProduct[];
  selected: number[];
  onToggle: (id: number) => void;
  full?: boolean;
  label: string;
}> = ({ products, selected, onToggle, full, label }) => {
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const f = fold(q.trim());
    if (!f) return products;
    return products.filter(p => fold(p.name).includes(f) || fold(p.category_name || '').includes(f));
  }, [q, products]);
  return (
    <div className="rounded-xl border border-line">
      <div className="relative border-b border-line p-2">
        <Search className="pointer-events-none absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-t4" aria-hidden="true" />
        <input type="search" value={q} onChange={e => setQ(e.target.value)} aria-label={label}
          placeholder="Αναζήτηση προϊόντος…" className={`${COMPACT_INPUT} w-full pl-9`} />
      </div>
      <div className="max-h-56 overflow-y-auto p-1.5">
        {products.length === 0 && <p className="p-2 text-sm text-t3">Δεν βρέθηκαν προϊόντα στον κεντρικό κατάλογο.</p>}
        {products.length > 0 && shown.length === 0 && <p className="p-2 text-sm text-t3">Κανένα αποτέλεσμα.</p>}
        {shown.map(p => {
          const on = selected.includes(p.id);
          const disabled = !on && !!full;
          return (
            <button key={p.id} type="button" disabled={disabled} aria-pressed={on} onClick={() => onToggle(p.id)}
              className={`${PICK_ROW} pl-2.5 ${pickTone(on)}`}>
              <PickBox on={on} />
              <span className="truncate">{p.name}</span>
              {p.category_name && <span className="hidden truncate text-xs text-t3 sm:inline">· {p.category_name}</span>}
              {/* Αποσυρμένα προϊόντα του κεντρικού καταλόγου μπορεί να είναι πολλά: γκρι, όχι πορτοκαλί. */}
              {!p.is_available && <span className="ml-auto shrink-0"><StatusChip tone="muted">μη διαθέσιμο</StatusChip></span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/** Η προεπισκόπηση: όσο πιο κοντά γίνεται στο παράθυρο που βλέπει ο ταμίας. */
// Ίδια εμφάνιση με το shared/UpsellPrompt.tsx του ταμείου, σε μικρότερη κλίμακα (χωρίς τιμή:
// η τιμή είναι του καταστήματος/τιμοκαταλόγου, όχι του κεντρικού καταλόγου). Το κεχριμπαρένιο των
// κουτιών είναι ο χαρακτήρας εκείνου του παραθύρου — γι' αυτό μένει, ενώ η υπόλοιπη σελίδα δεν έχει χρώμα.
const PopupPreview: React.FC<{ message: string; names: string[] }> = ({ message, names }) => {
  const cols = names.length === 1 ? 'grid-cols-1 max-w-[12rem] mx-auto'
    : names.length === 2 || names.length === 4 ? 'grid-cols-2'
    : 'grid-cols-2 sm:grid-cols-3';
  return (
    <div className="rounded-2xl border border-line bg-surface-0 shadow-sm overflow-hidden" data-testid="upsell-preview">
      <div className="px-4 pt-4 pb-3 text-center">
        <div className="mx-auto w-9 h-9 rounded-xl bg-amber-500/15 flex items-center justify-center mb-2">
          <Sparkles className="h-5 w-5 text-amber-600 dark:text-amber-400" aria-hidden="true" />
        </div>
        {message.trim()
          ? <p className="text-base sm:text-lg font-bold text-t1 leading-snug break-words">{message.trim()}</p>
          : <p className="text-base italic text-t4">Το μήνυμα εμφανίζεται εδώ</p>}
      </div>
      <div className="px-4 pb-3">
        {names.length === 0 ? (
          <div className="text-sm text-t4 italic text-center py-3 border border-dashed border-line rounded-xl">
            Διάλεξε προϊόντα για να εμφανιστούν εδώ
          </div>
        ) : (
          <div className={`grid gap-2 ${cols}`}>
            {names.map((n, i) => (
              <div key={i}
                className="min-h-[64px] rounded-xl border-2 border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center text-center p-2">
                <span className="text-sm font-semibold text-t1 leading-tight break-words">{n}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="px-4 py-2.5 border-t border-line bg-surface-1">
        <div className="w-full h-10 rounded-xl bg-surface-2 text-t1 text-sm font-semibold flex items-center justify-center select-none">
          Όχι, ευχαριστώ
        </div>
      </div>
    </div>
  );
};

/** Ανάλυση μιας πρότασης (ανά κατάστημα ή ανά ταμία) μέσα στην ανοιγμένη γραμμή της αναφοράς. */
const Breakdown: React.FC<{
  title: string;
  /** Όνομα της πρώτης στήλης για τους αναγνώστες οθόνης (στην οθόνη το λέει ήδη ο τίτλος). */
  labelHeader: string;
  rows: Array<Stat & { key: string; label: string }>;
}> = ({ title, labelHeader, rows }) => (
  <div className="min-w-0">
    <h4 className={`${SUB_TITLE} mb-2`}>{title}</h4>
    {rows.length === 0 ? (
      <p className="text-xs text-t3">Καμία κίνηση στο διάστημα.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          {/* Το RoleLight βάφει κάθε thead του διαχειριστή surface-1· εδώ θα έβγαινε λωρίδα μέσα στη γραμμή. */}
          <thead style={{ background: 'transparent' }}>
            <tr className="border-b border-line">
              <th scope="col" className="py-1.5 pr-2 text-left font-semibold text-t3"><span className="sr-only">{labelHeader}</span></th>
              <th scope="col" className="py-1.5 px-2 text-right font-semibold text-t3">Εμφ.</th>
              <th scope="col" className="py-1.5 px-2 text-right font-semibold text-t3">Αποδ.</th>
              <th scope="col" className="py-1.5 px-2 text-right font-semibold text-t3">%</th>
              <th scope="col" className="py-1.5 px-2 text-right font-semibold text-t3">Τεμ.</th>
              <th scope="col" className="py-1.5 pl-2 text-right font-semibold text-t3">Τζίρος</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.key} className="border-b border-line last:border-0">
                <td className="py-1.5 pr-2 text-t2 break-words">{r.label}</td>
                <td className="py-1.5 px-2 text-right tabular-nums text-t2">{fmtNum(r.shown)}</td>
                <td className="py-1.5 px-2 text-right tabular-nums text-t2">{fmtNum(r.accepted)}</td>
                <td className="py-1.5 px-2 text-right tabular-nums text-t2">{fmtRate(r.rate)}</td>
                <td className="py-1.5 px-2 text-right tabular-nums text-t2">{fmtNum(r.items)}</td>
                <td className="py-1.5 pl-2 text-right tabular-nums text-t1 whitespace-nowrap">{fmtEur(r.revenue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const SKELETON_WIDTHS = ['46%', '62%', '38%'];

/**
 * Πρώτη φόρτωση: σκελετός γραμμών μέσα στη σελίδα (όχι σκέτος τροχός). Χωρίς διακόπτη ως τότε:
 * θα έδειχνε «Κλειστό» πριν μάθουμε τι ισχύει.
 */
const ListSkeleton: React.FC = () => (
  <div className="border-t border-line" data-testid="upsell-loading">
    <span className="sr-only" role="status">Φόρτωση…</span>
    <div className="-mx-3 divide-y divide-line" aria-hidden="true">
      {SKELETON_WIDTHS.map((w, i) => (
        <div key={i} className="flex items-start gap-3 px-3 py-5">
          <span className="lp-skel shrink-0" style={{ width: 28, height: 28, borderRadius: 999 }} />
          <div className="min-w-0 flex-1 pt-1">
            <span className="lp-skel" style={{ width: w }} />
            <span className="lp-skel lp-skel-sm" style={{ width: '72%' }} />
            <span className="lp-skel lp-skel-sm" style={{ width: '54%' }} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ── Η οθόνη ──────────────────────────────────────────────────────────────────

const OrgUpsellManager: React.FC<Props> = ({ orgId, canEdit, canAdmin }) => {
  const uid = useId();
  const [enabled, setEnabled] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [stores, setStores] = useState<StoreLite[]>([]);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expandedStores, setExpandedStores] = useState<number | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Suggestion | null>(null);
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM });
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const formErrorRef = useRef<HTMLDivElement>(null);

  const [deleteTarget, setDeleteTarget] = useState<Suggestion | null>(null);
  const [deleting, setDeleting] = useState(false);
  const deleteReturnRef = useRef<HTMLElement | null>(null);

  const [repRange, setRepRange] = useState<DateRangeValue>(() => presetRange('last30'));
  const repFrom = repRange.from;
  const repTo = repRange.to;
  const [repStores, setRepStores] = useState<number[]>([]);
  const [repStoresOpen, setRepStoresOpen] = useState(false);
  const [report, setReport] = useState<Report | null>(null);
  const [repLoading, setRepLoading] = useState(false);
  const [repError, setRepError] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const repSeq = useRef(0);

  const today = useMemo(() => athensDate(0), []);

  const load = useCallback(async () => {
    try {
      const r = await organizationAPI.getUpsell(orgId);
      setEnabled(r?.enabled === true);
      const list: Suggestion[] = [...(r?.suggestions || [])];
      list.sort((a, b) => a.priority - b.priority || a.id - b.id);
      setSuggestions(list);
      setLoadState('ready');
    } catch {
      setError(LOAD_FAILED);
      // Πρώτη φόρτωση: ούτε λίστα ούτε διακόπτης (που θα έλεγε «Κλειστό» χωρίς να το ξέρουμε), μόνο
      // «Δοκιμάστε ξανά». Ανανέωση μετά από ενέργεια: η λίστα μένει όπως ήταν, με το μήνυμα από πάνω.
      setLoadState(prev => (prev === 'ready' ? 'ready' : 'error'));
    }
  }, [orgId]);

  useEffect(() => { setLoadState('loading'); load(); }, [load]);

  const retryLoad = () => {
    setError(null);
    setLoadState('loading');
    load();
  };

  // Κατάλογος/καταστήματα: μόνο για ονόματα και επιλογές. Αν αποτύχουν, η οθόνη δείχνει «#id».
  useEffect(() => {
    let alive = true;
    organizationAPI.getCatalogProducts(orgId)
      .then((r: any) => {
        if (!alive) return;
        setProducts((r?.products || r || []).map((x: any) => ({
          id: x.id, name: x.name, category_name: x.category_name ?? null, is_available: x.is_available !== false,
        })));
      })
      .catch(() => { if (alive) setProducts([]); });
    organizationAPI.getCatalogCategories(orgId)
      .then((r: any) => { if (alive) setCategories(buildCategoryTree(r?.categories || r || [])); })
      .catch(() => { if (alive) setCategories([]); });
    organizationAPI.getOrgStores(orgId)
      .then((r: any) => {
        if (!alive) return;
        setStores((r?.stores || r || []).map((s: any) => ({ id: s.id, name: s.display_name || s.name })));
      })
      .catch(() => { if (alive) setStores([]); });
    return () => { alive = false; };
  }, [orgId]);

  const loadReport = useCallback(async () => {
    if (!repFrom || !repTo) return;
    if (repFrom > repTo) { setRepError('Η ημερομηνία «Από» είναι μετά την «Έως»'); return; }
    const seq = ++repSeq.current;
    setRepLoading(true);
    setRepError(null);
    try {
      const r = await organizationAPI.getUpsellReport(orgId, repFrom, repTo, repStores);
      if (seq === repSeq.current) setReport(r);
    } catch {
      if (seq === repSeq.current) setRepError('Αποτυχία φόρτωσης αποτελεσμάτων');
    } finally {
      if (seq === repSeq.current) setRepLoading(false);
    }
  }, [orgId, repFrom, repTo, repStores]);

  useEffect(() => { loadReport(); }, [loadReport]);

  // Το σφάλμα της φόρμας είναι στην κορυφή του συρταριού· το «Δημιουργία» είναι κάτω. Αν ο χρήστης έχει
  // κυλήσει ως το ωράριο, το φέρνουμε στο μάτι (το jsdom δεν έχει scrollIntoView — γι' αυτό το «?.»).
  useEffect(() => {
    if (formError) formErrorRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [formError]);

  // Επιβεβαίωση διαγραφής: Esc = «Άκυρο» (όχι όσο σβήνει). Στο κλείσιμο το focus γυρίζει στο 🗑 της
  // γραμμής, όπως γυρίζει στη γραμμή όταν κλείνει το συρτάρι.
  useEffect(() => {
    if (!deleteTarget) {
      const back = deleteReturnRef.current;
      deleteReturnRef.current = null;
      if (back && back.isConnected) back.focus();
      return undefined;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || deleting) return;
      e.preventDefault();
      setDeleteTarget(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [deleteTarget, deleting]);

  const productById = useMemo(() => new Map<number, CatalogProduct>(products.map(p => [p.id, p])), [products]);
  const categoryById = useMemo(() => new Map<number, CatalogCategory>(categories.map(c => [c.id, c])), [categories]);
  const storeById = useMemo(() => new Map<number, StoreLite>(stores.map(s => [s.id, s])), [stores]);
  const productName = (id: number) => productById.get(id)?.name || `#${id}`;
  const categoryLabel = (id: number) => categoryById.get(id)?.label || `#${id}`;
  const storeName = (id: number) => storeById.get(id)?.name || `#${id}`;

  // ── Ενέργειες ──

  const toggleEnabled = async () => {
    if (!canAdmin || toggling) return;
    setToggling(true);
    setError(null);
    try {
      const r = await organizationAPI.setUpsellEnabled(orgId, !enabled);
      setEnabled(r?.enabled === true);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Αποτυχία αλλαγής του διακόπτη');
    } finally {
      setToggling(false);
    }
  };

  /**
   * Αλλαγή σειράς: νέα προτεραιότητα = θέση στη λίστα. Συνήθως αλλάζουν μόνο οι δύο που
   * άλλαξαν θέση· με ίσες προτεραιότητες (π.χ. όλες 0) ξαναγράφονται όσες χρειάζεται.
   */
  const move = async (index: number, dir: -1 | 1) => {
    const j = index + dir;
    if (!canEdit || busy || j < 0 || j >= suggestions.length) return;
    const order = [...suggestions];
    [order[index], order[j]] = [order[j], order[index]];
    const changed = order.map((s, i) => ({ s, priority: i })).filter(x => x.s.priority !== x.priority);
    setBusy(true);
    setError(null);
    setSuggestions(order.map((s, i) => ({ ...s, priority: i })));
    try {
      for (const { s, priority } of changed) {
        await organizationAPI.updateUpsellSuggestion(orgId, s.id, toPayload(formFrom(s), priority));
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Αποτυχία αλλαγής σειράς');
    } finally {
      await load();
      setBusy(false);
    }
  };

  const setActive = async (s: Suggestion, isActive: boolean) => {
    if (!canEdit || busy) return;
    setBusy(true);
    setError(null);
    try {
      await organizationAPI.updateUpsellSuggestion(orgId, s.id, toPayload({ ...formFrom(s), is_active: isActive }, s.priority));
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Αποτυχία αποθήκευσης');
    } finally {
      setBusy(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setFormError(null);
    setShowForm(true);
  };
  const openEdit = (s: Suggestion) => {
    setEditing(s);
    setForm(formFrom(s));
    setFormError(null);
    setShowForm(true);
  };

  const save = async () => {
    const v = validate(form);
    if (v) { setFormError(v); return; }
    setSaving(true);
    setFormError(null);
    try {
      if (editing) {
        await organizationAPI.updateUpsellSuggestion(orgId, editing.id, toPayload(form, editing.priority));
      } else {
        // Νέα πρόταση = τελευταία στη σειρά· ο χρήστης την ανεβάζει με τα βελάκια.
        const next = suggestions.length > 0 ? Math.max(...suggestions.map(s => s.priority)) + 1 : 0;
        await organizationAPI.createUpsellSuggestion(orgId, toPayload(form, Math.min(next, 9999)));
      }
      setShowForm(false);
      await load();
      loadReport();
    } catch (e: any) {
      setFormError(e?.response?.data?.error || 'Αποτυχία αποθήκευσης');
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (s: Suggestion, trigger: HTMLElement) => {
    deleteReturnRef.current = trigger;
    setDeleteTarget(s);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !canAdmin) return;
    setDeleting(true);
    setError(null);
    try {
      await organizationAPI.deleteUpsellSuggestion(orgId, deleteTarget.id);
      setDeleteTarget(null);
      await load();
      loadReport();
    } catch (e: any) {
      setDeleteTarget(null);
      setError(e?.response?.data?.error || 'Αποτυχία διαγραφής');
    } finally {
      setDeleting(false);
    }
  };

  const deactivateInstead = async () => {
    const s = deleteTarget;
    setDeleteTarget(null);
    if (s) await setActive(s, false);
  };

  // ── Περιλήψεις για τις γραμμές ──

  const scheduleSummary = (s: Suggestion) => {
    const parts: string[] = [];
    if (s.starts_at && s.ends_at) parts.push(`${fmtDate(s.starts_at)} – ${fmtDate(s.ends_at)}`);
    else if (s.starts_at) parts.push(`από ${fmtDate(s.starts_at)}`);
    else if (s.ends_at) parts.push(`έως ${fmtDate(s.ends_at)}`);
    const days = s.days_of_week;
    parts.push(days && days.length > 0 && days.length < 7
      ? DAY_CHIPS.filter(([v]) => days.includes(v)).map(([, l]) => l).join(', ')
      : 'κάθε μέρα');
    if (s.time_from && s.time_to) {
      parts.push(`${s.time_from}–${s.time_to}${s.time_from > s.time_to ? ' (περνά τα μεσάνυχτα)' : ''}`);
    } else {
      parts.push('όλες τις ώρες');
    }
    return parts.join(' · ');
  };

  const triggerSummary = (s: Suggestion) => {
    if (s.trigger_mode !== 'cart_contains') return 'Πάντα';
    const items = [
      ...s.trigger_org_product_ids.map(productName),
      ...s.trigger_org_category_ids.map(id => `κατηγορία ${categoryLabel(id)}`),
    ];
    return `Μόνο αν το καλάθι έχει: ${items.join(', ')}`;
  };

  const storesFor = (sid: number) => (report?.by_store || [])
    .filter(r => r.suggestion_id === sid)
    .map(r => ({ ...r, key: `s${r.store_id}`, label: r.store }));
  const cashiersFor = (sid: number) => (report?.by_cashier || [])
    .filter(r => r.suggestion_id === sid)
    .map(r => ({ ...r, key: `e${r.employee_id ?? 0}`, label: r.cashier || '—' }));

  // ── Φόρμα: βοηθοί ──

  const toggleIn = (list: number[], id: number) =>
    (list.includes(id) ? list.filter(x => x !== id) : [...list, id]);

  const moveProduct = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= form.org_product_ids.length) return;
    const ids = [...form.org_product_ids];
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setForm({ ...form, org_product_ids: ids });
  };

  const crossesMidnight = !!(form.time_from && form.time_to && form.time_from > form.time_to);

  // ── Η φόρμα (στο συρτάρι) ──

  const ids = {
    name: `${uid}-name`,
    message: `${uid}-message`,
    trigger: `${uid}-trigger`,
    fromDate: `${uid}-from-date`,
    toDate: `${uid}-to-date`,
    days: `${uid}-days`,
    fromTime: `${uid}-from-time`,
    toTime: `${uid}-to-time`,
  };
  const nameLen = form.name.trim().length;
  const messageLen = form.message.trim().length;
  const productCount = form.org_product_ids.length;

  // Χωρίς <form>, όπως πριν: το Enter στην αναζήτηση προϊόντος θα «αποθήκευε» την πρόταση.
  const formBody = (
    <div className="space-y-6" data-testid="upsell-form">
      {formError && (
        <div ref={formErrorRef}>
          <Banner tone="danger" role="alert" testId="upsell-form-error">{formError}</Banner>
        </div>
      )}

      {/* Όνομα */}
      <SheetField label="Όνομα (εσωτερικό)" htmlFor={ids.name}>
        <input id={ids.name} type="text" value={form.name} autoFocus={!editing}
          onChange={e => setForm({ ...form, name: e.target.value })} aria-describedby={`${ids.name}-hint`}
          placeholder="π.χ. Χυμός με το πρωινό" className={INPUT_CLS} />
        <p id={`${ids.name}-hint`} className="xs-field-hint">
          Το βλέπεις μόνο εσύ, στη λίστα και στα αποτελέσματα — όχι ο ταμίας.
          {nameLen > NAME_MAX && <>{' '}<StatusChip tone="danger">{nameLen}/{NAME_MAX}</StatusChip></>}
        </p>
      </SheetField>

      {/* Μήνυμα */}
      <SheetField label="Μήνυμα προς τον ταμία" htmlFor={ids.message}>
        <textarea id={ids.message} rows={2} value={form.message}
          onChange={e => setForm({ ...form, message: e.target.value })} aria-describedby={`${ids.message}-hint`}
          placeholder="π.χ. Να σας βάλω και έναν φρέσκο χυμό;" className={`${INPUT_CLS} resize-y`} />
        <div className="mt-1.5 flex items-start justify-between gap-3">
          <p id={`${ids.message}-hint`} className="text-xs leading-[17px] text-t3">Γράψε το όπως θα το πει ο ταμίας στον πελάτη.</p>
          <span className="shrink-0 text-xs tabular-nums text-t3" data-testid="upsell-message-counter">
            {messageLen > MESSAGE_MAX
              ? <StatusChip tone="danger">{`${messageLen}/${MESSAGE_MAX}`}</StatusChip>
              : `${messageLen}/${MESSAGE_MAX}`}
          </span>
        </div>
      </SheetField>

      {/* Προϊόντα + προεπισκόπηση */}
      <div className="space-y-5 border-t border-line pt-6">
        <SheetField label={(
          <>
            <span>Προϊόντα που προτείνονται</span>
            <span className="ml-auto pl-3 text-xs font-normal tabular-nums text-t3">
              {productCount >= MAX_PRODUCTS
                ? <StatusChip tone="warn">{`${productCount}/${MAX_PRODUCTS}`}</StatusChip>
                : `${productCount}/${MAX_PRODUCTS}`}
            </span>
          </>
        )}>
          <p className="mb-3 text-xs leading-[17px] text-t3">
            Από τον κεντρικό κατάλογο — σε κάθε κατάστημα αντιστοιχίζονται στο δικό του προϊόν, με την κανονική
            τιμή. Με αυτή τη σειρά εμφανίζονται στο ταμείο.
          </p>
          {productCount > 0 && (
            <ol className="mb-3 space-y-1">
              {form.org_product_ids.map((pid, i) => (
                <li key={pid} className="flex items-center gap-1 rounded-lg border border-line bg-surface-2 py-0.5 pl-3 pr-1 text-sm">
                  <span className="w-5 shrink-0 text-xs tabular-nums text-t3">{i + 1}.</span>
                  <span className="min-w-0 flex-1 truncate text-t1">{productName(pid)}</span>
                  <button type="button" onClick={() => moveProduct(i, -1)} disabled={i === 0}
                    className={ROW_ICON_BTN} aria-label={`Πιο πάνω: ${productName(pid)}`}>
                    <ChevronUp className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button type="button" onClick={() => moveProduct(i, 1)} disabled={i === productCount - 1}
                    className={ROW_ICON_BTN} aria-label={`Πιο κάτω: ${productName(pid)}`}>
                    <ChevronDown className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button type="button"
                    onClick={() => setForm({ ...form, org_product_ids: form.org_product_ids.filter(x => x !== pid) })}
                    className={ROW_DANGER_BTN} aria-label={`Αφαίρεση: ${productName(pid)}`}>
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ol>
          )}
          <ProductPicker products={products} selected={form.org_product_ids}
            full={productCount >= MAX_PRODUCTS} label="Αναζήτηση προϊόντος για πρόταση"
            onToggle={id => setForm({ ...form, org_product_ids: toggleIn(form.org_product_ids, id) })} />
        </SheetField>

        <SheetField label="Προεπισκόπηση — έτσι το βλέπει ο ταμίας">
          <PopupPreview message={form.message} names={form.org_product_ids.map(productName)} />
        </SheetField>
      </div>

      {/* Κανόνας εμφάνισης */}
      <div className="border-t border-line pt-6">
        <fieldset>
          <legend className="xs-label">Πότε εμφανίζεται</legend>
          <div className="flex flex-wrap gap-2">
            <SheetInherit name={ids.trigger} checked={form.trigger_mode === 'always'}
              onSelect={() => setForm({ ...form, trigger_mode: 'always' })} label="Πάντα (εκτός αν είναι ήδη στο καλάθι)" />
            <SheetInherit name={ids.trigger} checked={form.trigger_mode === 'cart_contains'}
              onSelect={() => setForm({ ...form, trigger_mode: 'cart_contains' })} label="Μόνο αν το καλάθι έχει…" />
          </div>
          {form.trigger_mode === 'cart_contains' && (
            <div className="mt-4 space-y-4 border-l-2 border-line pl-4">
              <div>
                <span className="mb-1.5 block text-xs font-medium text-t3">
                  Προϊόντα ({form.trigger_org_product_ids.length})
                </span>
                <ProductPicker products={products} selected={form.trigger_org_product_ids}
                  label="Αναζήτηση προϊόντος-προϋπόθεσης"
                  onToggle={id => setForm({ ...form, trigger_org_product_ids: toggleIn(form.trigger_org_product_ids, id) })} />
              </div>
              <div>
                <span className="mb-1.5 block text-xs font-medium text-t3">
                  ή κατηγορίες ({form.trigger_org_category_ids.length}) — μια κατηγορία καλύπτει και τις υποκατηγορίες της
                </span>
                <div className="max-h-48 overflow-y-auto rounded-xl border border-line p-1.5">
                  {categories.length === 0 && <p className="p-2 text-sm text-t3">Δεν βρέθηκαν κατηγορίες.</p>}
                  {categories.map(c => {
                    const on = form.trigger_org_category_ids.includes(c.id);
                    return (
                      <button key={c.id} type="button" aria-pressed={on}
                        onClick={() => setForm({ ...form, trigger_org_category_ids: toggleIn(form.trigger_org_category_ids, c.id) })}
                        style={{ paddingLeft: 10 + c.depth * 18 }}
                        className={`${PICK_ROW} ${pickTone(on)}`}>
                        <PickBox on={on} />
                        <span className="truncate">{c.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </fieldset>
      </div>

      {/* Κανάλια και καταστήματα */}
      <div className="space-y-5 border-t border-line pt-6">
        <fieldset>
          <legend className="xs-label">Κανάλια</legend>
          <div className="flex flex-wrap gap-2">
            {CHANNELS.map(c => {
              const on = form.channels.includes(c.id);
              return (
                <label key={c.id} className={`${chipClass(on)} cursor-pointer`}>
                  <input type="checkbox" checked={on} className={CHECKBOX}
                    onChange={() => setForm({
                      ...form,
                      channels: form.channels.includes(c.id) ? form.channels.filter(x => x !== c.id) : [...form.channels, c.id],
                    })} />
                  {c.label}
                </label>
              );
            })}
          </div>
        </fieldset>

        <fieldset>
          <legend className="xs-label">Καταστήματα</legend>
          <label className={`${chipClass(form.all_stores)} cursor-pointer`}>
            <input type="checkbox" checked={form.all_stores} className={CHECKBOX}
              onChange={e => setForm({ ...form, all_stores: e.target.checked })} />
            Όλα τα καταστήματα
            <span className="text-xs font-normal text-t3">(και όσα προστεθούν αργότερα)</span>
          </label>
          {!form.all_stores && (
            <div className="mt-3">
              <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                <button type="button" className={LINK_BTN} onClick={() => setForm({ ...form, store_ids: stores.map(s => s.id) })}>
                  Επιλογή όλων
                </button>
                {form.store_ids.length > 0 && (
                  <button type="button" className={LINK_BTN} onClick={() => setForm({ ...form, store_ids: [] })}>
                    Καθαρισμός
                  </button>
                )}
                <span className="text-xs tabular-nums text-t3">{form.store_ids.length} επιλεγμένα</span>
              </div>
              <div className="grid max-h-52 grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-1 overflow-y-auto rounded-xl border border-line p-1.5">
                {stores.length === 0 && <p className="p-2 text-sm text-t3">Δεν βρέθηκαν καταστήματα.</p>}
                {stores.map(st => {
                  const checked = form.store_ids.includes(st.id);
                  return (
                    <label key={st.id}
                      className={`flex min-h-[40px] cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-sm transition-colors ${checked ? 'bg-brand-soft font-medium text-t1' : 'text-t2 hover:bg-surface-2'}`}>
                      <input type="checkbox" checked={checked} className={CHECKBOX}
                        onChange={() => setForm({ ...form, store_ids: toggleIn(form.store_ids, st.id) })} />
                      <span className="truncate">{st.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </fieldset>
      </div>

      {/* Ωράριο */}
      <div className="border-t border-line pt-6">
        <fieldset>
          <legend className="xs-label">Ωράριο (κενό = πάντα)</legend>
          <div className="space-y-4">
            <div className="xs-grid2 max-w-lg">
              <SheetField label="Από ημερομηνία" htmlFor={ids.fromDate}>
                <input id={ids.fromDate} type="date" value={form.starts_at}
                  onChange={e => setForm({ ...form, starts_at: e.target.value })} className={INPUT_CLS} />
              </SheetField>
              <SheetField label="Έως ημερομηνία" htmlFor={ids.toDate}>
                <input id={ids.toDate} type="date" value={form.ends_at}
                  onChange={e => setForm({ ...form, ends_at: e.target.value })} className={INPUT_CLS} />
              </SheetField>
            </div>
            <div>
              <span id={ids.days} className="xs-label">Ημέρες (καμία = κάθε μέρα)</span>
              <div className="flex flex-wrap gap-1.5" role="group" aria-labelledby={ids.days}>
                {DAY_CHIPS.map(([v, l]) => {
                  const on = form.days_of_week.includes(v);
                  return (
                    <button key={v} type="button" aria-pressed={on}
                      onClick={() => setForm({ ...form, days_of_week: toggleIn(form.days_of_week, v) })}
                      className={`${chipClass(on)} min-w-[52px] justify-center`}>
                      {l}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="xs-grid2 max-w-sm">
              <SheetField label="Από ώρα" htmlFor={ids.fromTime}>
                <input id={ids.fromTime} type="time" value={form.time_from}
                  onChange={e => setForm({ ...form, time_from: e.target.value })} className={INPUT_CLS} />
              </SheetField>
              <SheetField label="Έως ώρα" htmlFor={ids.toTime}>
                <input id={ids.toTime} type="time" value={form.time_to}
                  onChange={e => setForm({ ...form, time_to: e.target.value })} className={INPUT_CLS} />
              </SheetField>
            </div>
            <p className={`text-xs leading-[17px] ${crossesMidnight ? 'font-medium text-t1' : 'text-t3'}`}>
              {crossesMidnight
                ? `Περνά τα μεσάνυχτα: ισχύει από ${form.time_from} έως ${form.time_to} της επόμενης μέρας.`
                : 'Ώρα καταστήματος. Αν το «από» είναι μετά το «έως» (π.χ. 22:00–02:00), περνά τα μεσάνυχτα.'}
            </p>
          </div>
        </fieldset>
      </div>

      {/* Ενεργή */}
      <div className="border-t border-line pt-6">
        <SheetRow
          title="Ενεργή"
          subtitle="Μόνο οι ενεργές εμφανίζονται στα ταμεία — και μόνο όσο ο διακόπτης του οργανισμού είναι ανοιχτός."
          control={({ labelId, descriptionId }) => (
            <SettingsSwitch checked={form.is_active} onChange={next => setForm({ ...form, is_active: next })}
              labelId={labelId} describedById={descriptionId} />
          )}
        />
      </div>
    </div>
  );

  // ── Η σελίδα ──

  const count = suggestions.length;

  return (
    <ListPage
      eyebrow="Κατάλογος"
      title="Προτάσεις πώλησης"
      subtitle={(
        <>
          Ένα μήνυμα και έως {MAX_PRODUCTS} προϊόντα που βλέπει ο ταμίας μόλις πατήσει «Ολοκλήρωση» (Take Away)
          ή «Checkout» (τηλεφωνικό delivery). Το ταμείο δείχνει <strong className="font-semibold text-t2">μία</strong> πρόταση:
          την πρώτη της λίστας που ταιριάζει — η σειρά αλλάζει με τα βελάκια.
        </>
      )}
      testId="org-upsell"
    >
      {loadState === 'loading' && <ListSkeleton />}

      {loadState === 'error' && <LoadFailed message={error || LOAD_FAILED} onRetry={retryLoad} />}

      {loadState === 'ready' && (
        <>
          {/* ── Διακόπτης οργανισμού: γραμμή του «Φύλλου» ── */}
          <div className="border-t border-line py-5" data-testid="upsell-switch-row">
            <SheetRow
              title="Προτάσεις πώλησης στο ταμείο"
              subtitle="Ένας διακόπτης για όλα τα καταστήματα του ομίλου: ανοιχτός = τα ταμεία δείχνουν τις ενεργές προτάσεις."
              note={!canAdmin ? 'Τον διακόπτη τον αλλάζει μόνο ο ιδιοκτήτης ή διαχειριστής του οργανισμού.' : undefined}
              control={({ labelId, descriptionId }) => (
                <span className="flex items-center gap-2.5">
                  {/* Η κατάσταση τη λέει ήδη ο διακόπτης (aria-checked)· το κείμενο είναι για το μάτι. */}
                  <span className={`text-sm font-medium ${enabled ? 'text-t1' : 'text-t3'}`} aria-hidden="true">
                    {enabled ? 'Ανοιχτό' : 'Κλειστό'}
                  </span>
                  <SettingsSwitch checked={enabled} onChange={() => { toggleEnabled(); }}
                    disabled={!canAdmin || toggling} labelId={labelId} describedById={descriptionId} />
                </span>
              )}
            />
            {!enabled && (
              <Banner tone="warn" className="mt-4" testId="upsell-off-notice">
                Ο διακόπτης είναι κλειστός: <strong className="font-semibold">κανένα ταμείο</strong> δεν δείχνει προτάσεις πώλησης, ακόμη κι αν
                κάποιες είναι «Ενεργές» παρακάτω.
              </Banner>
            )}
          </div>

          <ListToolbar
            actions={canEdit ? (
              <button type="button" className={PRIMARY_BTN} onClick={openNew}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Νέα πρόταση
              </button>
            ) : null}
            summary={`${count} ${count === 1 ? 'πρόταση' : 'προτάσεις'}`}
            testId="upsell-toolbar"
          />

          {error && !showForm && (
            <Banner tone="danger" role="alert" className="mb-3">{error}</Banner>
          )}

          {/* ── Λίστα: η σειρά της είναι η σειρά που τις ελέγχει το ταμείο ── */}
          {count === 0 ? (
            <EmptyState icon={Sparkles} title="Καμία πρόταση πώλησης ακόμα." testId="upsell-empty" />
          ) : (
            <div className="-mx-3" data-testid="upsell-list">
              <div className="flex items-center gap-3 border-b border-line-strong px-3 py-2 text-[12.5px] font-semibold leading-[18px] text-t3" aria-hidden="true">
                <span className="w-10 shrink-0 text-center">Σειρά</span>
                <span className="min-w-0 flex-1">Πρόταση</span>
              </div>
              <ol className="divide-y divide-line" aria-label="Προτάσεις πώλησης, με τη σειρά που τις ελέγχει το ταμείο">
                {suggestions.map((s, i) => {
                  const expired = !!s.ends_at && s.ends_at.slice(0, 10) < today;
                  const upcoming = !!s.starts_at && s.starts_at.slice(0, 10) > today;
                  const storeNames = s.store_ids.map(storeName);
                  return (
                    <li key={s.id} data-testid={`upsell-card-${s.id}`}
                      className={`flex items-start gap-3 px-3 py-4 transition-colors hover:bg-surface-2 ${s.is_active ? '' : 'opacity-75'}`}>
                      {/* Σειρά προτεραιότητας */}
                      <div className="flex w-10 shrink-0 flex-col items-center gap-0.5">
                        {canEdit && (
                          <button type="button" onClick={() => move(i, -1)} disabled={busy || i === 0}
                            aria-label={`Πιο πάνω: ${s.name}`} title="Πιο πάνω (εμφανίζεται νωρίτερα)" className={ROW_ICON_BTN}>
                            <ChevronUp className="h-4 w-4" aria-hidden="true" />
                          </button>
                        )}
                        <span title="Σειρά προτεραιότητας"
                          className={`flex h-7 w-7 items-center justify-center rounded-full bg-surface-3 text-xs font-semibold tabular-nums text-t2 ${canEdit ? '' : 'mt-1'}`}>
                          {i + 1}
                        </span>
                        {canEdit && (
                          <button type="button" onClick={() => move(i, 1)} disabled={busy || i === count - 1}
                            aria-label={`Πιο κάτω: ${s.name}`} title="Πιο κάτω" className={ROW_ICON_BTN}>
                            <ChevronDown className="h-4 w-4" aria-hidden="true" />
                          </button>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                          {/* pt: η πρώτη γραμμή στο ύψος των κουμπιών (36px) δεξιά και του ▲ αριστερά */}
                          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 pt-[7px]">
                            <span className="break-words text-[15px] font-semibold leading-[22px] text-t1">{s.name}</span>
                            <StatusChip tone={s.is_active ? 'ok' : 'muted'}>{s.is_active ? 'Ενεργή' : 'Ανενεργή'}</StatusChip>
                            {expired && <StatusChip tone="danger">Έληξε</StatusChip>}
                            {upcoming && s.starts_at && <StatusChip tone="brand">Ξεκινά {fmtDate(s.starts_at)}</StatusChip>}
                          </div>
                          {(canEdit || canAdmin) && (
                            <div className="flex shrink-0 items-center gap-1">
                              {canEdit && (
                                <button type="button" onClick={() => setActive(s, !s.is_active)} disabled={busy} className={ASIDE_BTN}>
                                  {s.is_active ? 'Απενεργοποίηση' : 'Ενεργοποίηση'}
                                </button>
                              )}
                              {canEdit && (
                                <button type="button" onClick={() => openEdit(s)} disabled={busy} className={ROW_ICON_BTN}
                                  title="Επεξεργασία" aria-label={`Επεξεργασία: ${s.name}`}>
                                  <Pencil className="h-4 w-4" aria-hidden="true" />
                                </button>
                              )}
                              {canAdmin && (
                                <button type="button" onClick={e => openDelete(s, e.currentTarget)} disabled={busy} className={ROW_DANGER_BTN}
                                  title="Διαγραφή" aria-label={`Διαγραφή: ${s.name}`}>
                                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>

                        <p className="mt-1 break-words text-sm text-t2">«{s.message}»</p>

                        {s.org_product_ids.length > 0 && (
                          <ul className="mt-2 flex flex-wrap gap-1.5" aria-label="Προϊόντα της πρότασης">
                            {s.org_product_ids.map(pid => (
                              <li key={pid} className="rounded-md bg-surface-3 px-2 py-0.5 text-xs font-medium leading-5 text-t2">
                                {productName(pid)}
                              </li>
                            ))}
                          </ul>
                        )}

                        <dl className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1 text-[13px] leading-[19px]">
                          <Meta label="Κανάλια">
                            {CHANNELS.filter(c => s.channels.includes(c.id)).map(c => c.short).join(' · ')}
                          </Meta>
                          <Meta label="Καταστήματα">
                            {s.store_ids.length === 0 ? 'Όλα τα καταστήματα' : (
                              // Τα ονόματα με πάτημα: στο tablet δεν υπάρχει hover για το title.
                              <button type="button" title={storeNames.join(', ')}
                                onClick={() => setExpandedStores(expandedStores === s.id ? null : s.id)}
                                aria-expanded={expandedStores === s.id}
                                className="font-medium text-t1 underline decoration-dotted underline-offset-2 hover:decoration-solid">
                                {s.store_ids.length === 1 ? '1 κατάστημα' : `${s.store_ids.length} καταστήματα`}
                              </button>
                            )}
                          </Meta>
                          <Meta label="Πότε">{scheduleSummary(s)}</Meta>
                          <Meta label="Εμφάνιση">{triggerSummary(s)}</Meta>
                        </dl>

                        {expandedStores === s.id && s.store_ids.length > 0 && (
                          <ul className="mt-2 flex flex-wrap gap-1.5" aria-label="Καταστήματα της πρότασης">
                            {storeNames.map((n, k) => (
                              <li key={s.store_ids[k]} className="rounded-md bg-surface-3 px-2 py-0.5 text-xs leading-5 text-t2">{n}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          {/* ── Αποτελέσματα ── */}
          <section className="mt-10 border-t border-line pt-6" aria-label="Αποτελέσματα" data-testid="upsell-results">
            <GroupTitle aside={repLoading ? <Loader2 className="h-4 w-4 animate-spin text-t4" aria-hidden="true" /> : null}>
              Αποτελέσματα
            </GroupTitle>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <DateRange from={repFrom} to={repTo} onChange={setRepRange} presets={REPORT_PRESETS} allowAll={false}
                label="Περίοδος αποτελεσμάτων" testId="upsell-report-period" />
              {stores.length > 1 && (
                // Ίδια όψη με τα φίλτρα του «Πίνακα»: όταν φιλτράρει, περίγραμμα/κείμενο στο χρώμα της εφαρμογής.
                <button type="button" onClick={() => setRepStoresOpen(o => !o)} aria-expanded={repStoresOpen}
                  aria-controls={repStoresOpen ? `${uid}-report-stores` : undefined}
                  className={`inline-flex h-11 items-center gap-2 rounded-xl border bg-surface-2 px-3.5 text-sm transition-colors ${
                    repStores.length > 0
                      ? 'border-brand font-semibold text-brand shadow-[inset_0_0_0_1px_hsl(var(--brand))]'
                      : 'border-line-strong text-t1 hover:bg-surface-3'}`}>
                  <span>Καταστήματα</span>
                  <span className={repStores.length > 0 ? '' : 'text-t3'}>
                    {repStores.length === 0 ? `Όλα (${stores.length})` : `${repStores.length} επιλεγμένα`}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-t3 transition-transform ${repStoresOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                </button>
              )}
            </div>

            {stores.length > 1 && repStoresOpen && (
              <div id={`${uid}-report-stores`} className="mt-3 rounded-xl border border-line p-1.5" data-testid="upsell-report-stores">
                {repStores.length > 0 && (
                  <div className="px-2 pb-1.5 pt-1">
                    <button type="button" className={LINK_BTN} onClick={() => setRepStores([])}>Όλα τα καταστήματα</button>
                  </div>
                )}
                <div className="grid max-h-52 grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-1 overflow-y-auto">
                  {stores.map(st => {
                    const checked = repStores.includes(st.id);
                    return (
                      <label key={st.id}
                        className={`flex min-h-[40px] cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-sm transition-colors ${checked ? 'bg-brand-soft font-medium text-t1' : 'text-t2 hover:bg-surface-2'}`}>
                        <input type="checkbox" checked={checked} className={CHECKBOX}
                          onChange={() => setRepStores(toggleIn(repStores, st.id))} />
                        <span className="truncate">{st.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {repError && <Banner tone="danger" role="alert" className="mt-3">{repError}</Banner>}

            {report && report.suggestions.length === 0 && (
              <p className="py-8 text-center text-sm text-t3">Δεν υπάρχουν προτάσεις για αναφορά.</p>
            )}

            {report && report.suggestions.length > 0 && (
              <div className="-mx-3 mt-3 overflow-x-auto" data-testid="upsell-report">
                <table className="w-full min-w-[560px] border-collapse text-sm" aria-busy={repLoading || undefined}>
                  <thead>
                    <tr className="border-b border-line-strong">
                      <th scope="col" className={`${TH} text-left`}>Πρόταση</th>
                      <th scope="col" className={`${TH} text-right`}>Εμφανίσεις</th>
                      <th scope="col" className={`${TH} text-right`}>Αποδοχές</th>
                      <th scope="col" className={`${TH} text-right`}>Ποσοστό</th>
                      <th scope="col" className={`${TH} text-right`}>Τεμάχια</th>
                      <th scope="col" className={`${TH} text-right`}>Τζίρος</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.suggestions.map(row => {
                      const open = expandedRow === row.id;
                      return (
                        <React.Fragment key={row.id}>
                          <tr className="border-b border-line" data-testid={`upsell-report-row-${row.id}`}>
                            <td className="px-3 py-2.5">
                              <button type="button" onClick={() => setExpandedRow(open ? null : row.id)} aria-expanded={open}
                                className="inline-flex max-w-full items-center gap-1.5 rounded-md text-left font-medium text-t1 hover:text-brand">
                                <ChevronRight className={`h-4 w-4 shrink-0 text-t4 transition-transform ${open ? 'rotate-90' : ''}`} aria-hidden="true" />
                                <span className="break-words">{row.name}</span>
                                {!row.is_active && <StatusChip tone="muted">Ανενεργή</StatusChip>}
                              </button>
                            </td>
                            <td className={`${TD} text-t2`}>{fmtNum(row.shown)}</td>
                            <td className={`${TD} text-t2`}>{fmtNum(row.accepted)}</td>
                            <td className={`${TD} font-medium text-t1`}>{fmtRate(row.rate)}</td>
                            <td className={`${TD} text-t2`}>{fmtNum(row.items)}</td>
                            <td className={`${TD} whitespace-nowrap font-medium text-t1`}>{fmtEur(row.revenue)}</td>
                          </tr>
                          {open && (
                            <tr className="border-b border-line" data-testid={`upsell-report-details-${row.id}`}>
                              <td colSpan={6} className="bg-surface-2 px-3 py-4">
                                <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-6">
                                  <Breakdown title="Ανά κατάστημα" labelHeader="Κατάστημα" rows={storesFor(row.id)} />
                                  <Breakdown title="Ανά ταμία" labelHeader="Ταμίας" rows={cashiersFor(row.id)} />
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <p className="mt-4 text-xs leading-[17px] text-t3">
              Αποδοχή = παραγγελία που περιείχε προϊόν της πρότασης. Δεν μετρούν ακυρωμένες και εκπαιδευτικές παραγγελίες.
            </p>
          </section>
        </>
      )}

      {/* ── Φόρμα: συρτάρι δεξιά ── */}
      <DetailPanel
        open={showForm}
        onClose={() => setShowForm(false)}
        width="lg"
        title={editing ? 'Επεξεργασία πρότασης πώλησης' : 'Νέα πρόταση πώλησης'}
        subtitle={editing ? editing.name : undefined}
        footer={(
          <>
            <button type="button" className={GHOST_BTN} onClick={() => setShowForm(false)}>Άκυρο</button>
            <button type="button" className={PRIMARY_BTN} onClick={save} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {editing ? 'Αποθήκευση' : 'Δημιουργία'}
            </button>
          </>
        )}
        testId="upsell-panel"
      >
        {showForm && formBody}
      </DetailPanel>

      {/* ── Επιβεβαίωση διαγραφής: σβήνει και τα στατιστικά ── */}
      {deleteTarget && (
        <div className="lp-overlay flex items-center justify-center p-4" data-testid="upsell-delete-overlay">
          <div className="lp-scrim" aria-hidden="true" />
          <div role="alertdialog" aria-modal="true" aria-label="Διαγραφή πρότασης πώλησης" aria-describedby={`${uid}-delete-desc`}
            className="relative w-full max-w-md rounded-2xl border border-line bg-surface-1 p-6 shadow-2xl">
            <h2 className="break-words text-lg font-bold leading-snug text-t1">Διαγραφή της πρότασης «{deleteTarget.name}»;</h2>
            <div id={`${uid}-delete-desc`} className="mt-2 space-y-2 text-sm leading-relaxed">
              <p className="text-t2">
                Θα σβηστούν <strong className="font-semibold text-t1">και όλα τα στατιστικά</strong> της (εμφανίσεις, αποδοχές, τζίρος) — δεν επανέρχονται.
              </p>
              <p className="text-t3">
                {deleteTarget.is_active ? (
                  <>
                    Αν θέλεις απλώς να σταματήσει να εμφανίζεται στα ταμεία, προτίμησε την <strong className="font-semibold text-t2">απενεργοποίηση</strong>:
                    τα αποτελέσματα μένουν.
                  </>
                ) : (
                  <>
                    Είναι ήδη <strong className="font-semibold text-t2">ανενεργή</strong> και δεν εμφανίζεται στα ταμεία — αν δεν σε ενοχλεί στη λίστα,
                    άφησέ τη έτσι για να μείνουν τα αποτελέσματα.
                  </>
                )}
              </p>
            </div>
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              {/* Focus στο ακίνδυνο κουμπί: ένα Enter από συνήθεια δεν σβήνει τίποτα. */}
              <button type="button" autoFocus onClick={() => setDeleteTarget(null)} disabled={deleting} className={GHOST_BTN}>
                Άκυρο
              </button>
              <button type="button" onClick={confirmDelete} disabled={deleting} className={DANGER_GHOST_BTN}>
                {deleting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                Οριστική διαγραφή
              </button>
              {canEdit && deleteTarget.is_active && (
                <button type="button" onClick={deactivateInstead} disabled={deleting} className={PRIMARY_BTN}>
                  Απενεργοποίηση
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </ListPage>
  );
};

export default OrgUpsellManager;
